import { useEffect, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import { ExternalUserServiceIntegrationUpdate } from '@zocks-communications/client-sdk-js'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetUserActiveSessionsQuery } from '../../../../../store/media/media'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { Button } from '../../../../../components/Button/Button'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ringCentralSettingsDefaultValues, type RingCentralSettingsFormValues } from './models'
import { ExternalUserServiceIntegrationConfig } from '../../../../../models/User'

const THREE_HOUR_IN_SECONDS = 3 * 60 * 60

export const RingCentralSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)!

  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [startedAfter] = useState(() => Math.floor(Date.now() / 1000) - THREE_HOUR_IN_SECONDS)
  const { data: activeSessions, isLoading: activeSessionLoading } = useGetUserActiveSessionsQuery(
    {
      accountId,
      userId: authenticatedUser.id,
      kind: 'external',
      startedAfter,
      pageRequest: { page: 0, size: 10 },
    },
    { refetchOnMountOrArgChange: true },
  )
  const [updateUserIntegration, { isLoading: isUpdating }] = useUpdateUserIntegrationMutation()

  const form = useForm<RingCentralSettingsFormValues>({
    defaultValues: ringCentralSettingsDefaultValues as RingCentralSettingsFormValues,
  })
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = form

  const meetingInProgress = useMemo(() => {
    if (!activeSessionLoading && activeSessions) {
      return activeSessions.data.length > 0
    }
    return false
  }, [activeSessionLoading, activeSessions])

  const ringcentralIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.RINGCENTRAL)
  }, [userData])

  useEffect(() => {
    if (ringcentralIntegration) {
      const config = ringcentralIntegration.configuration
      reset({
        integrationType: config.type,
      })
    }
  }, [reset, ringcentralIntegration])

  const handleSave = async (data: RingCentralSettingsFormValues) => {
    const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.RINGCENTRAL)
    if (integration) {
      const payload = {
        configuration: {
          type: data.integrationType,
        } as ExternalUserServiceIntegrationConfig,
      } as ExternalUserServiceIntegrationUpdate

      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload,
      })
    }
  }

  if (isUserLoading || !userData) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container className='p-0'>
      <Row className='mb-4'>
        <Col>
          <h3>Set your call processing method</h3>
          <p className='text-muted'>Manage how Zocks processes your RingCentral meetings.</p>
        </Col>
      </Row>

      {meetingInProgress ? (
        <Row className='mb-4'>
          <Col>
            <Alert variant='warning'>You cannot change your RingCentral settings while you have an active meeting in progress on RingCentral.</Alert>
          </Col>
        </Row>
      ) : null}

      <Form onSubmit={handleSubmit(handleSave)}>
        <Container className='d-flex flex-column gap-4 p-0'>
          <Row>
            <Col>
              <Controller
                name='integrationType'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Group className='mb-3'>
                      <div className='d-flex flex-column gap-2'>
                        <Form.Check
                          type='radio'
                          id='integrationType-bot'
                          label={
                            <>
                              <span>Merge Zocks into your calls</span>
                              <Form.Text className='d-block text-muted'>
                                When you start a call, Zocks calls you separately. Use RingCentral&apos;s merge feature to combine both calls to add your AI
                                Assistant to the conversation. You can manually add or remove the assistant during any live call.
                              </Form.Text>
                            </>
                          }
                          checked={field.value === 'bot'}
                          onChange={() => field.onChange('bot')}
                          disabled={meetingInProgress}
                        />
                        <Form.Check
                          type='radio'
                          id='integrationType-recording'
                          label={
                            <>
                              <span>Process recordings</span>
                              <Form.Text className='d-block text-muted'>
                                You can manually record phone calls, and let Zocks process the recording when the meeting ends. Optionally you can turn on
                                on-demand or automatic call recording in the RingCentral Admin Portal, to start recording at the beginning of each call.
                              </Form.Text>
                            </>
                          }
                          checked={field.value === 'recording'}
                          onChange={() => field.onChange('recording')}
                          disabled={meetingInProgress}
                        />
                      </div>
                    </Form.Group>
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <div className='d-flex gap-3 justify-content-end mb-4'>
                <Button type='button' variant='secondary' onClick={() => reset()} disabled={isUpdating}>
                  Reset
                </Button>
                <Button type='submit' variant='primary' loading={isUpdating} disabled={meetingInProgress || !isDirty}>
                  Save Changes
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </Form>
    </Container>
  )
}
